/**
 * PWA-side wrapper around the AutoRelease native plugin.
 *
 * Type shapes are duplicated from
 * packages/mobile/src/plugins/autoRelease/definitions.ts — yes,
 * intentionally. They form the contract between the JS and native sides;
 * if we cross-imported, the web tsconfig would walk into packages/mobile
 * and choke on @capacitor/core (which is a packages/mobile dep, not a
 * packages/web dep). Long-term we could move these into packages/shared;
 * for now, duplication is the cheaper path.
 *
 * On native at runtime, the dynamic import resolves to the registered
 * Capacitor plugin via packages/mobile. On the browser, we return a
 * tombstone that no-ops every call.
 */
import { isNativePlatform } from './runtime';

export type PermissionState =
  | 'unknown'
  | 'prompt'
  | 'granted'
  | 'granted_when_in_use'
  | 'denied'
  | 'denied_permanently';

export interface BuildingAnchor {
  buildingId: string;
  latitude:   number;
  longitude:  number;
  timezone:   string;
}

export interface ArmOptions {
  releaseHours:     number;
  windowStartLocal: string;
  windowEndLocal:   string;
  // ISO weekday integers (1=Mon..7=Sun) the worker should treat as working
  // days. If omitted, the native plugin keeps its built-in default (Israeli
  // Sun-Thu) for backwards compatibility.
  workweekDays?:    number[];
  thresholdMeters?: number;
  apiBaseUrl:       string;
}

export interface AutoReleaseStatus {
  state:               'idle' | 'armed' | 'evaluating' | 'pending' | 'released' | 'unauthorized';
  lastEvaluatedAt:     string | null;
  lastTriggeredAt:     string | null;
  lastDistanceMeters:  number | null;
  scheduledNextRunAt:  string | null;
}

export interface ReleaseTriggeredEvent {
  clientEventId:  string;
  distanceMeters: number;
  accuracyMeters: number;
  evaluatedAt:    string;
}

interface PluginShape {
  setBuildingAnchor(p: BuildingAnchor):           Promise<void>;
  requestPermissions():                            Promise<{ state: PermissionState }>;
  checkPermissions():                              Promise<{ state: PermissionState }>;
  arm(o: ArmOptions):                              Promise<void>;
  disarm():                                        Promise<void>;
  getStatus():                                     Promise<AutoReleaseStatus>;
  addListener(
    eventName: 'releaseTriggered',
    listener:  (e: ReleaseTriggeredEvent) => void,
  ): Promise<{ remove: () => Promise<void> }>;
  removeAllListeners(): Promise<void>;
}

let cached: PluginShape | null = null;

async function load(): Promise<PluginShape> {
  if (cached) return cached;
  if (!isNativePlatform()) {
    cached = browserTombstone();
    return cached;
  }
  // The plugin is registered by the native shell into the global bridge as
  // `window.Capacitor.Plugins.AutoRelease`. Going through the global avoids
  // two failure modes a bare `import('@lotngo/mobile/...')` runs into:
  //   1. The browser cannot resolve bare module specifiers at runtime —
  //      `/* @vite-ignore */` preserves the string but there's no resolver
  //      to satisfy it, so the dynamic import throws.
  //   2. Even if it could resolve, an npm-wrapper proxy registers its
  //      listeners on a different plugin instance than native's
  //      notifyListeners dispatches through (same registry-mismatch trap
  //      that bit PushNotifications). The global IS the authoritative one.
  const plugin = (globalThis as any).Capacitor?.Plugins?.AutoRelease as PluginShape | undefined;
  if (!plugin) throw new Error('Capacitor AutoRelease plugin not available');
  cached = plugin;
  return cached;
}

function browserTombstone(): PluginShape {
  return {
    async setBuildingAnchor() { /* no-op */ },
    async requestPermissions() { return { state: 'denied_permanently' as const }; },
    async checkPermissions()   { return { state: 'denied_permanently' as const }; },
    async arm()    { /* no-op */ },
    async disarm() { /* no-op */ },
    async getStatus() {
      return {
        state:              'idle' as const,
        lastEvaluatedAt:    null,
        lastTriggeredAt:    null,
        lastDistanceMeters: null,
        scheduledNextRunAt: null,
      };
    },
    async addListener(_event: 'releaseTriggered', _listener: (e: ReleaseTriggeredEvent) => void) {
      void _listener;
      return { remove: async () => {} };
    },
    async removeAllListeners() { /* no-op */ },
  };
}

export const autoReleasePlugin = {
  setBuildingAnchor: async (p: BuildingAnchor) => (await load()).setBuildingAnchor(p),
  requestPermissions: async () => (await load()).requestPermissions(),
  checkPermissions:   async () => (await load()).checkPermissions(),
  arm:    async (o: ArmOptions) => (await load()).arm(o),
  disarm: async () => (await load()).disarm(),
  getStatus: async () => (await load()).getStatus(),
  addReleaseListener: async (cb: (e: ReleaseTriggeredEvent) => void) =>
    (await load()).addListener('releaseTriggered', cb),
};
